import {
  DuperbrowseItem,
  DuperbrowseStream,
  DuperbrowseItemId,
  DuperbrowseNormalizedItem,
  DuperbrowseItemSchema,
  DuperbrowseSimpleStream,
} from './types';
import { PapiDeviation } from '@wix/da-papi-types';
import { Location } from 'history';
import {
  getDeviationPath,
  getDeviationResourceType,
} from '../../DeviationViews/_helpers';
import matchRoutes from '../../redux/routing/matchRoutes';
import {
  getDeviationEntityId,
  deviationSchema,
} from '../../redux/normalizr/schemas/deviation';
import {
  DAMZ_PAGE_DUPERBROWSE_ITEMID,
  FEEDBACK_PAGE_DUPERBROWSE_ITEMID,
} from './constants';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import {
  BiData,
  BiEvent,
  NavigationArrowClickBiEvent,
} from '@wix/da-bi/pkg/events';

export function isDeviation(
  item: DuperbrowseItem | DuperbrowseNormalizedItem
): item is PapiDeviation {
  return item.hasOwnProperty('deviationId');
}

export function isDeviationStream(stream: DuperbrowseSimpleStream) {
  return stream.items.every(isDeviation);
}

export function isNormalizedStream(stream: DuperbrowseSimpleStream) {
  return (
    stream.items.every(item => isNumber(item) || isString(item)) &&
    !!stream.itemSchema
  );
}

export function getStreamItemId(
  item: DuperbrowseItem | DuperbrowseNormalizedItem
): DuperbrowseItemId | undefined {
  if (item === undefined) {
    return;
  }
  if (isDeviation(item)) {
    return item.deviationId;
  }
  if (isNumber(item) || isString(item)) {
    return item;
  }
}

export function getNormalizedStreamItemId(
  item: DuperbrowseItem | DuperbrowseNormalizedItem
): DuperbrowseItemId | undefined {
  if (isNumber(item) || isString(item)) {
    return item;
  }

  if (isDeviation(item)) {
    return getDeviationEntityId(item);
  }
}

export function getStreamItemUrl(item: DuperbrowseItem) {
  if (isDeviation(item)) {
    return getDeviationPath(item);
  }
  if (item === FEEDBACK_PAGE_DUPERBROWSE_ITEMID) {
    return '/notifications';
  }
  if (item === DAMZ_PAGE_DUPERBROWSE_ITEMID) {
    return '/messages';
  }
}

export function getHtmlIdForStreamItem(
  stream: DuperbrowseStream,
  itemId: DuperbrowseItemId
) {
  return `duper-${stream.streamId}-${itemId}`;
}

export function isDuperbrowseLocation(
  location: Location<any>,
  ignoreState = false
) {
  // if there is known state, it's ours for sure
  if (
    !ignoreState &&
    location.state &&
    (location.state.hasOwnProperty('duperbrowseState') ||
      location.state.hasOwnProperty('duperbrowseRoot'))
  ) {
    return true;
  }

  const routeMatches = matchRoutes(
    {
      key: 'devpagelike',
      path: [
        '/:username/art/:deviationIdLike',
        '/:username/journal/:deviationIdLike',
        '/:username/status-update/:deviationIdLike',
        '/:username/poll/:deviationIdLike',
        '/:username/commission/:deviationIdLike',
        '/:username/tier/:deviationIdLike',
      ],
      exact: true,
    },
    location.pathname,
    false
  );

  return routeMatches.length > 0;
}

export function getDuperbrowseItemSchema(schema: DuperbrowseItemSchema) {
  switch (schema) {
    case 'deviation':
      return deviationSchema;
    default:
  }
}

export function getBasicBiDataForDuperItem(item: DuperbrowseItem) {
  if (isDeviation(item)) {
    return {
      itemid: item.deviationId,
      typeid: getDeviationResourceType(item),
    };
  }
}

export function getBiEventData(
  currentItem: DuperbrowseItem,
  direction: 'prev' | 'previous' | 'next',
  nextItem?: DuperbrowseItem
): BiEvent {
  const { itemid, typeid } = getBasicBiDataForDuperItem(currentItem) ?? {};
  const { itemid: next_itemid } =
    (nextItem && getBasicBiDataForDuperItem(nextItem)) ?? {};

  return BiData<NavigationArrowClickBiEvent>({
    evid: 300,
    direction: direction === 'prev' ? 'previous' : direction,
    itemid: itemid ?? 0,
    typeid: typeid ?? 0,
    next_itemid: next_itemid,
  });
}

export function getStashBiEventData(
  currentItem: DuperbrowseItem,
  direction: 'prev' | 'previous' | 'next'
) {
  const { itemid, typeid } = getBasicBiDataForDuperItem(currentItem) ?? {};

  return BiData<NavigationArrowClickBiEvent>({
    evid: 300,
    direction: direction === 'prev' ? 'previous' : direction,
    itemid: itemid ?? 0,
    typeid: typeid ?? 0,
  });
}
